import React, { useState, useEffect } from 'react';
import { SitecoreContext as SitecoreContextProvider } from '@sitecore-jss/sitecore-jss-react';
import { Route, Routes, useParams } from 'react-router-dom';
import { isJwtExpired } from 'jwt-check-expiration';
import { createBrowserHistory } from 'history';

import { isWebBrowser, closeAuthchatWindow } from 'core/utils/global-utils';
import { AI } from 'core/services/AppInsights';
import { LOGOUT_STATUS, SITECORE_PAGES } from 'core/constants';
import {
	getAccessToken,
	clearLocalStorage,
	clearSessionCookie,
	setDisplayLogoutSuccessFlag,
} from 'core/browserStorage';
import { redirectTalRefreshToLogin } from 'core/utils/routeHandler-utils';
import componentFactory from 'jss-boilerplate/temp/componentFactory';
import RouteHandler from 'jss-boilerplate/RouteHandler';

import LoadingScreen from './LoadingScreen';

// This is the main JSX entry point of the app invoked by the renderer (server or client rendering).
// By default the app's normal rendering is delegated to <RouteHandler> that handles the loading of JSS route data.

// support languages in the URL prefix
// e.g. /da-DK/path, or /en/path, or /path
export const routePatterns = [
	'/:lang([a-z]{2}-[A-Z]{2})/:sitecoreRoute/*',
	'/:lang([a-z]{2})/:sitecoreRoute/*',
	'/:sitecoreRoute/*',
];
const LANGUAGE_REG_EXP = /^\/?(([a-z]{2}-[A-Z]{2})|([a-z]{2}))(\/|$)/g;

if (isWebBrowser) {
	// initialize App Insights only inside browser
	const history = createBrowserHistory({ basename: '' });
	AI.initialize({ history });
}

export const parseRouteParams = (url) => {
	const route = url.replace(routePatterns, '');
	const language = url.match(LANGUAGE_REG_EXP);

	return {
		route: route.startsWith('/') ? route : `/${route}`,
		language: language ? language[0].replace(/\//g, '') : undefined,
	};
};

const JssRoute = (props) => {
	const params = useParams();
	const url = params['*'];

	return (
		<RouteHandler
			{...parseRouteParams(url)}
			url={url}
			isSSR={props.isSSR}
			onRouteDataLoaded={props.onRouteDataLoaded}
		/>
	);
};

// wrap the app with:
// SitecoreContext: provides component resolution and context services via withSitecoreContext
// Router: provides a basic routing setup that will resolve Sitecore item routes and allow for language URL prefixes.

const AppRoot = (props) => {
	const { path, Router, ssrState } = props;
	const { context: sitecoreContext } = ssrState ? ssrState?.sitecore : {};
	const [isLoading, setIsLoading] = useState(true);
	// Handler to be called when route data is loaded
	const handleRouteDataLoaded = () => setIsLoading(false);

	// Set isLoading to false when sitecoreContext is available
	useEffect(() => {
		if (sitecoreContext) {
			setIsLoading(false);
		}
	}, [sitecoreContext]);

	//This will check if the token is still valid or not and will redirect user to respective url
	useEffect(() => {
		if (getAccessToken()) {
			const isAccesTokenExpired = isJwtExpired(getAccessToken());
			if (isAccesTokenExpired) {
				//close the auth chat window if access token expires
				if (typeof closeAuthchatWindow === 'function') {
					closeAuthchatWindow();
				} else {
					console.warn(
						'closeAuthchatWindow is not a function, please check the import or definition.',
					);
				}

				sessionStorage.clear();
				clearLocalStorage();
				clearSessionCookie();
				setDisplayLogoutSuccessFlag(LOGOUT_STATUS.LOGOUT_JWT);

				// Redirect to login page with goto param if the user was on TAL page when token expired
				if (path === SITECORE_PAGES.TAL_REFRESH) {
					redirectTalRefreshToLogin();

					return;
				}

				window.location.assign(SITECORE_PAGES.LOGIN);
			}
		}
	}, []);

	// Child component to handle LPTag logic inside SitecoreContext
	function LpTagInitializer({ setIsLoading, sitecoreContext }) {
		useEffect(() => {
			if (!sitecoreContext) return;
			// added dummy GTM id if Sitecore fails to return metadata.
			const storeId = sitecoreContext?.metadata?.storeId ?? '2072666';
			// 'LPTag' integration (LivePerson)
			(function (d, s, i) {
				const f = d.getElementsByTagName(s)[0],
					j = d.createElement(s);
				j.async = true;
				j.innerHTML = `
					(window.lpTag = window.lpTag || {}),
					"undefined" == typeof window.lpTag._tagCount
						? ((window.lpTag = {
							wl: lpTag.wl || null,
							scp: lpTag.scp || null,
							site: ${i} || "",
							section: lpTag.section || "",
							tagletSection: lpTag.tagletSection || null,
							autoStart: lpTag.autoStart !== !1,
							ovr: lpTag.ovr || {},
							_v: "1.10.0",
							_tagCount: 1,
							protocol: "https:",
							events: {
								bind: function (t, e, i) {
									lpTag.defer(function () {
										lpTag.events.bind(t, e, i);
									}, 0);
								},
								trigger: function (t, e, i) {
									lpTag.defer(function () {
										lpTag.events.trigger(t, e, i);
									}, 1);
								},
							},
							defer: function (t, e) {
								0 === e
									? ((this._defB = this._defB || []), this._defB.push(t))
								: 1 === e
								? ((this._defT = this._defT || []), this._defT.push(t))
								: ((this._defL = this._defL || []), this._defL.push(t));
							},
							load: function (t, e, i) {
								var n = this;
								setTimeout(function () {
									n._load(t, e, i);
								}, 0);
							},
							_load: function (t, e, i) {
								var n = t;
								t ||
									(n =
										this.protocol +
										"//" +
										(this.ovr && this.ovr.domain
											? this.ovr.domain
											: "lptag.liveperson.net") +
										"/tag/tag.js?site=" +
										this.site);
								var o = document.createElement("script");
								o.setAttribute("charset", e ? e : "UTF-8"),
								i && o.setAttribute("id", i),
								o.setAttribute("src", n),
								document.getElementsByTagName("head").item(0).appendChild(o);
							},
							init: function () {
								(this._timing = this._timing || {}),
								(this._timing.start = new Date().getTime());
								var t = this;
								window.attachEvent
									? window.attachEvent("onload", function () {
										t._domReady("domReady");
									})
								: (window.addEventListener(
										"DOMContentLoaded",
										function () {
											t._domReady("contReady");
										},
										!1
									),
									window.addEventListener(
										"load",
										function () {
											t._domReady("domReady");
										},
										!1
									)),
								"undefined" === typeof window._lptStop && this.load();
							},
							start: function () {
								this.autoStart = !0;
							},
							_domReady: function (t) {
								this.isDom ||
									((this.isDom = !0),
									this.events.trigger("LPT", "DOM_READY", { t: t })),
									(this._timing[t] = new Date().getTime());
							},
							vars: lpTag.vars || [],
							dbs: lpTag.dbs || [],
							ctn: lpTag.ctn || [],
							sdes: lpTag.sdes || [],
							hooks: lpTag.hooks || [],
							identities: lpTag.identities || [],
							ev: lpTag.ev || [],
						}),
						lpTag.init())
					: (window.lpTag._tagCount += 1);
				`;
				f.parentNode.insertBefore(j, f);
			})(document, 'script', storeId);
			setIsLoading(false);
		}, [sitecoreContext, setIsLoading]);

		return null;
	}

	if (isLoading) {
		return <LoadingScreen />;
	}

	return (
		<>
			<SitecoreContextProvider
				componentFactory={componentFactory}
				layoutData={ssrState}
			>
				<LpTagInitializer
					setIsLoading={setIsLoading}
					sitecoreContext={sitecoreContext}
				/>
				<Router location={path} context={{}}>
					<Routes>
						{routePatterns.map((routePattern) => (
							<Route
								key={routePattern}
								path="*"
								element={
									<JssRoute
										onRouteDataLoaded={handleRouteDataLoaded}
										isSSR={!!ssrState}
									/>
								}
							/>
						))}
					</Routes>
				</Router>
			</SitecoreContextProvider>
		</>
	);
};

export default AppRoot;
